import { Plan } from "@jugl-web/rest-api";
import { useTranslations } from "@jugl-web/utils";
import { useMemo } from "react";

const ADDON_ID_USERS = "jugl::users";
const ADDON_ID_AI_CREDITS = "jugl::ai_credits";
const ADDON_ID_KNOWLEDGE = "jugl::knowledge";

const getAddonCredits = (plan: Plan, addonId: string): number | null =>
  plan.default_addons.find((a) => a.id === addonId)?.credits ?? null;

const formatCredits = (credits: number): string =>
  credits >= 1000 ? credits.toLocaleString("en-US") : String(credits);

export const usePlansData = (plans: Plan[]) => {
  const { t } = useTranslations();

  const plansFeatures = useMemo(
    () =>
      plans.map((plan, index) => {
        const userCredits = getAddonCredits(plan, ADDON_ID_USERS) ?? 1;
        const aiCredits = getAddonCredits(plan, ADDON_ID_AI_CREDITS) ?? 0;
        const knowledgeCredits = getAddonCredits(plan, ADDON_ID_KNOWLEDGE) ?? 0;

        const isFree = plan.prices.every((p) => p.unit_amount === 0);
        const previousPlanName = index > 0 ? plans[index - 1].name : null;

        return {
          id: plan.id,
          planName: plan.name,
          subtitle: plan.description,
          header: previousPlanName
            ? t(
                {
                  id: "plans-data.everything-in-previous-plan",
                  defaultMessage: "Everything in {planName} +",
                },
                { planName: previousPlanName }
              )
            : t({
                id: "plans-data.plan-includes",
                defaultMessage: "Plan includes:",
              }),
          isFree,
          features: [
            {
              name: t(
                {
                  id: "plans-data.features.user-count",
                  defaultMessage:
                    "{count} {count, plural, one {user} other {users} }",
                },
                { count: userCredits }
              ),
            },
            {
              name: t(
                {
                  id: "plans-data.features.message-credits-for-ai-agent-per-month",
                  defaultMessage:
                    "{count} Message credits for AI agent per month",
                },
                { count: formatCredits(aiCredits) }
              ),
              tooltip: t({
                id: "plans-data.features.message-credits-tooltip",
                defaultMessage:
                  "Message credits are used by the AI Agent when it sends messages in the channels",
              }),
            },
            {
              name: t(
                {
                  id: "plans-data.features.ai-knowledge-mb",
                  defaultMessage:
                    "{count} {count, plural, one {MB} other {MB} } of AI Agent Knowledge",
                },
                { count: knowledgeCredits }
              ),
            },
            {
              name: t({
                id: "plans-data.features.chat-integrations",
                defaultMessage:
                  "Chat integration (WhatsApp, Instagram, Web chat, SMS chat)",
              }),
            },
            {
              name: t({
                id: "plans-data.features.agent-setup",
                defaultMessage: "Agent setup",
              }),
            },
          ],
        };
      }),
    [plans, t]
  );

  return { plansFeatures };
};
